import MaskedTextField from '@/common/components/MaskedTextField'
import Modal from '@/common/components/Modal'
import SelectController from '@/common/components/SelectController'
import TextArea from '@/common/components/TextArea'
import TextField from '@/common/components/TextField'
import { cnpjMask, nameMask, phoneMask } from '@/common/constants/masks.constant'
import { FormTransformers } from '@/common/utils/formTransformers'
import { commercialKindOptions } from '@/features/establishmentLeadsFunnel/constants/commercialKindOptions.constant'
import useCreateEstablishmentLead from '@/features/establishmentLeadsFunnel/hooks/useCreateEstablishmentLead'
import { CreateEstablishmentLeadValidationSchema, createEstablishmentLeadSchema } from '@/features/establishmentLeadsFunnel/schemas/create.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { BadgePlus } from 'lucide-react'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import Button from '../../../common/components/Button'

export const CreateModal = () => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)

  const {
    control,
    register,
    handleSubmit,
    reset: resetForm,
    setError,
    formState: { errors }
  } = useForm<CreateEstablishmentLeadValidationSchema>({
    resolver: zodResolver(createEstablishmentLeadSchema)
  })

  const onSubmit: SubmitHandler<CreateEstablishmentLeadValidationSchema> = (data) => {
    createEstablishmentLead(data)
  }

  const onSuccess = () => {
    resetForm()
    queryClient.invalidateQueries({ queryKey: ['establishmentLeads'] })
    setIsOpen(false)
  }

  const {
    mutate: createEstablishmentLead,
    isPending: createEstablishmentLeadIsPending,
  } = useCreateEstablishmentLead({
    onSuccess,
    onError: FormTransformers.errorsTransformer<CreateEstablishmentLeadValidationSchema>({ setError }),
  })

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      disabled={createEstablishmentLeadIsPending}
      Trigger={
        <Button
          name="open_create_establishment_modal"
          className="bg-transparent hover:bg-transparent p-0"
          operationKlassToCheckPermission={"Admin::Funnel::Establishment::Leads::Create"}
        >
          <BadgePlus color="#7CC4F8" strokeWidth={1.5} />
        </Button>
      }
      title="Cadastrando oportunidade"
      footer="Cadastrar"
      dataTestid="create_establishment_funnel"
      bodyClassName='max-h-[calc(90vh-72px-48px)] overflow-auto'
      body={(
        <form className="flex flex-col gap-4">
          <TextField
            label="Nome da empresa"
            markAsRequired
            errorMessage={errors?.display_name?.message || undefined}
            {...register('display_name')}
            disabled={createEstablishmentLeadIsPending}
          />
          <SelectController
            label="Tipo de estabelecimento"
            name="general_information.commercial_kind"
            className="w-full"
            control={control}
            options={commercialKindOptions}
            markAsRequired
            errorMessage={errors?.general_information?.commercial_kind?.message || undefined}
            isDisabled={createEstablishmentLeadIsPending}
          />
          <MaskedTextField
            label="CNPJ"
            markAsRequired
            mask={cnpjMask}
            errorMessage={errors?.general_information?.cnpj?.message || undefined}
            {...register('general_information.cnpj')}
            disabled={createEstablishmentLeadIsPending}
          />
          <div className="w-full flex gap-2">
            <MaskedTextField
              label="Nome do Contato"
              markAsRequired
              mask={nameMask}
              className="w-2/3"
              errorMessage={errors?.contacts?.full_name?.message}
              {...register('contacts.full_name')}
              disabled={createEstablishmentLeadIsPending}
            />
            <TextField
              label="Cargo"
              maxLength={32}
              className="w-1/3"
              errorMessage={errors?.contacts?.role_in_company?.message}
              {...register('contacts.role_in_company')}
              disabled={createEstablishmentLeadIsPending}
            />
          </div>
          <MaskedTextField
            label="Telefone do Contato"
            markAsRequired
            mask={phoneMask}
            errorMessage={errors?.contacts?.phone?.number?.message}
            {...register('contacts.phone.number')}
            disabled={createEstablishmentLeadIsPending}
          />
          <TextField
            label="Email de Contato"
            markAsRequired
            errorMessage={errors?.contacts?.email?.message}
            {...register('contacts.email')}
            disabled={createEstablishmentLeadIsPending}
          />
          <TextArea
            label="Suas observações"
            markAsRequired
            maxLength={1500}
            errorMessage={errors?.observations?.message}
            {...register('observations')}
            disabled={createEstablishmentLeadIsPending}
          />
        </form>
      )}
    />
  )
}
